import { useState, useMemo } from 'react';
import { Hotel, ExternalLink, GripVertical, MapPin, Loader, Sparkles } from 'lucide-react';
import { generateHotels } from '../../api/atlas';
import { useTokens } from '../../contexts/TokenContext';
import TokenGateModal from '../ui/TokenGateModal';
import { TokenCost } from '../ui/TokenBadge';
import { TOKEN_COSTS } from '../../constants/tokens';

// Unset until Alix signs up at partner.booking.com and adds it to Cloudflare Pages env vars — links work either way, just unmonetized until then.
const BOOKING_AID = process.env.REACT_APP_BOOKING_AID;

const getBookingUrl = (hotelName, city) => {
  const query = `${hotelName} ${city || ''}`.trim();
  const aidParam = BOOKING_AID ? `&aid=${BOOKING_AID}` : '';
  return `https://www.booking.com/searchresults.html?ss=${encodeURIComponent(query)}${aidParam}`;
};

const cleanCityName = (city) =>
  city.replace(/\s+(Prefecture|Province|Region|District|County|Metropolitan)$/i, '').trim();

function matchHotelToCity(hotelLocation, cities) {
  if (!hotelLocation) return null;
  const loc = hotelLocation.toLowerCase();
  return cities.find(c => loc.includes(c.toLowerCase())) || null;
}

export default function HotelsPanel({ accommodations, activities, itinerary, onSetHotelDropPrompt, onHotelsGenerated }) {
  const [draggingId, setDraggingId] = useState(null);
  const [generatingHotels, setGeneratingHotels] = useState(false);
  const [gateOpen, setGateOpen] = useState(false);
  const { canAfford, deductTokens } = useTokens();

  const cost = TOKEN_COSTS.GENERATE_HOTELS;

  const handleGenerateHotels = async () => {
    if (!itinerary?.id) return;
    if (!canAfford(cost)) { setGateOpen(true); return; }
    setGeneratingHotels(true);
    try {
      const data = await generateHotels(itinerary.id);
      deductTokens(cost);
      if (onHotelsGenerated) onHotelsGenerated(data.accommodations || []);
    } catch (e) {
      console.error('Hotel generation failed:', e);
    } finally {
      setGeneratingHotels(false);
    }
  };

  const cities = useMemo(() => {
    const fromActivities = [...new Set((activities || []).map(a => a.city_name).filter(Boolean))];
    if (fromActivities.length > 0) return fromActivities;
    return [...new Set((accommodations || []).map(h => h.location).filter(Boolean))];
  }, [activities, accommodations]);

  const hotelsByCity = useMemo(() => {
    const groups = {};
    const unmatched = [];
    (accommodations || []).forEach((hotel, idx) => {
      const h = { ...hotel, _idx: idx };
      const city = matchHotelToCity(hotel.location, cities);
      if (city) {
        if (!groups[city]) groups[city] = [];
        groups[city].push(h);
      } else {
        unmatched.push(h);
      }
    });
    if (unmatched.length > 0) {
      if (cities.length > 0) {
        unmatched.forEach((h, i) => {
          const city = cities[i % cities.length];
          if (!groups[city]) groups[city] = [];
          groups[city].push(h);
        });
      } else {
        unmatched.forEach(h => {
          const key = h.location || itinerary?.destination || 'Hotels';
          if (!groups[key]) groups[key] = [];
          groups[key].push(h);
        });
      }
    }
    return groups;
  }, [accommodations, cities, itinerary]);

  const hasHotels = accommodations && accommodations.length > 0;

  if (!hasHotels) {
    return (
      <div className="h-full flex flex-col items-center justify-center p-8 text-center bg-platinum-50">
        <div className="w-16 h-16 rounded-2xl bg-columbia-50 flex items-center justify-center mb-4">
          <Hotel size={28} className="text-columbia-400" />
        </div>
        <p className="text-sm font-bold text-charcoal-500 mb-1.5">No hotel suggestions yet</p>
        <p className="text-xs text-platinum-500 mb-4 leading-relaxed max-w-xs">
          Generate AI-curated hotel options for your destination.
        </p>
        <button
          onClick={handleGenerateHotels}
          disabled={generatingHotels || !itinerary?.id}
          className="flex items-center gap-2 px-5 py-2.5 bg-coral-400 hover:bg-coral-500 text-white text-sm font-semibold rounded-xl transition-colors disabled:opacity-50"
        >
          {generatingHotels ? <Loader size={14} className="animate-spin" /> : <Sparkles size={14} />}
          {generatingHotels ? 'Generating…' : 'Suggest Hotels'}
        </button>
        <TokenCost cost={cost} className="mt-1" />
        {gateOpen && (
          <TokenGateModal actionLabel="Hotel suggestions" cost={cost} onClose={() => setGateOpen(false)} />
        )}
      </div>
    );
  }

  const sectionsToShow = cities.length > 0 ? cities : Object.keys(hotelsByCity);

  const handleDragStart = (e, hotel, globalIdx) => {
    e.dataTransfer.setData('application/json', JSON.stringify({ ...hotel, __type: 'hotel' }));
    setDraggingId(`hotel-${globalIdx}`);
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {gateOpen && (
        <TokenGateModal actionLabel="Hotel suggestions" cost={cost} onClose={() => setGateOpen(false)} />
      )}
      <div className="flex-1 overflow-y-auto bg-platinum-50">
        <div className="p-3 space-y-3">
          {sectionsToShow.map(city => {
            const cityHotels = (hotelsByCity[city] || []).slice(0, 4);
            if (cityHotels.length === 0) return null;
            return (
              <div key={city} className="bg-white rounded-2xl shadow-sm overflow-hidden">
                {/* City header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-platinum-100">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-coral-50 flex items-center justify-center flex-shrink-0">
                      <MapPin size={15} className="text-coral-500" />
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-charcoal-600">{cleanCityName(city)}</h3>
                      <p className="text-xs text-platinum-400">Accommodation</p>
                    </div>
                  </div>
                  <a
                    href={getBookingUrl('', city)}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="text-xs font-bold px-3 py-1.5 rounded-full bg-[#003580] hover:bg-[#002a6e] transition-colors text-white"
                  >
                    Booking.com
                  </a>
                </div>

                <div className="divide-y divide-platinum-100">
                  {cityHotels.map((hotel) => {
                    const globalIdx = hotel._idx;
                    const isDragging = draggingId === `hotel-${globalIdx}`;
                    return (
                      <div
                        key={hotel.id || globalIdx}
                        draggable
                        onDragStart={(e) => handleDragStart(e, hotel, globalIdx)}
                        onDragEnd={() => setDraggingId(null)}
                        className={`p-4 transition-all ${isDragging ? 'opacity-40' : 'hover:bg-platinum-50'}`}
                      >
                        {/* Header row */}
                        <div className="flex items-start gap-2.5 mb-2">
                          <GripVertical size={13} className="text-platinum-300 flex-shrink-0 mt-1 cursor-grab" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <h4 className="text-sm font-bold text-charcoal-500 leading-snug">{hotel.name}</h4>
                                <p className="text-xs text-platinum-400 mt-0.5">
                                  {hotel.type ? `${hotel.type.charAt(0).toUpperCase() + hotel.type.slice(1)} · ` : ''}
                                  {hotel.location}
                                </p>
                              </div>
                              {hotel.price_per_night && (
                                <span className="text-sm font-bold text-columbia-600 whitespace-nowrap flex-shrink-0">
                                  ~${hotel.price_per_night}<span className="text-xs font-normal text-platinum-400">/night</span>
                                </span>
                              )}
                            </div>
                            {/* Description */}
                            {hotel.description && (
                              <p className="text-xs text-platinum-500 mt-1.5 leading-snug line-clamp-2">
                                {hotel.description}
                              </p>
                            )}
                            {/* Why recommended */}
                            {hotel.why_recommended && (
                              <p className="text-xs text-charcoal-400 mt-1 leading-snug italic border-l-2 border-coral-200 pl-2">
                                {hotel.why_recommended}
                              </p>
                            )}
                          </div>
                        </div>
                        {/* Actions */}
                        <div className="flex gap-2 ml-5">
                          <a
                            href={getBookingUrl(hotel.name, city)}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white bg-[#003580] hover:bg-[#002a6e] rounded-lg transition-colors"
                          >
                            <ExternalLink size={11} />
                            Book on Booking.com
                          </a>
                          {onSetHotelDropPrompt && (
                            <button
                              onClick={(e) => { e.stopPropagation(); onSetHotelDropPrompt({ hotel, dayNumber: 1 }); }}
                              className="flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-bold text-columbia-600 bg-columbia-50 hover:bg-columbia-100 rounded-lg transition-colors border border-columbia-200 flex-shrink-0"
                            >
                              <Hotel size={11} />
                              Add to trip
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
          <div className="flex items-center justify-between py-2 px-1">
            <p className="text-[10px] text-platinum-400">
              AI estimates · Book on <span className="font-semibold text-[#003580]">Booking.com</span>
            </p>
            <button
              onClick={handleGenerateHotels}
              disabled={generatingHotels}
              className="text-[10px] text-coral-500 hover:text-coral-600 font-semibold flex items-center gap-1 disabled:opacity-40"
            >
              {generatingHotels ? <Loader size={10} className="animate-spin" /> : <Sparkles size={10} />}
              {generatingHotels ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
